import { StreamReader } from "../lib/StreamReader";

/**
 * Event handler type for stream events
 */
export type StreamEventHandler = (data: unknown) => void;

/**
 * Map of event types to their handlers
 */
export interface StreamEventHandlers {
  [eventType: string]: StreamEventHandler;
}

/**
 * Service for processing streaming responses
 */
export class StreamProcessor {
  /**
   * Process a streaming response with event handlers
   *
   * @param response The API response containing a stream
   * @param signal AbortSignal for cancellation
   * @param eventHandlers Handlers for different event types
   */
  async processStream(
    response: Response,
    signal: AbortSignal,
    eventHandlers: StreamEventHandlers,
  ): Promise<void> {
    const reader = response.body?.getReader();

    if (!reader) {
      return;
    }

    const streamReader = new StreamReader(reader);
    const chunks = await streamReader.read();

    try {
      for await (const chunk of chunks) {
        // Check for cancellation after each chunk
        if (signal.aborted) break;

        if (!chunk || !chunk.data || !chunk.event) continue;

        try {
          const parsedData = JSON.parse(chunk.data);

          // Process the event if a handler exists
          const handler = eventHandlers[chunk.event];

          if (handler) {
            handler(parsedData);
          }
        } catch (error) {
          // Silent fail for parse errors as they're expected in some cases
          // Continue processing next chunks
        }
      }
    } catch (error) {
      // Don't throw if aborted - this is expected
      if (!signal.aborted) {
        throw error;
      }
    }
  }
}

// Export a singleton instance
export const streamProcessor = new StreamProcessor();
